import React, { Suspense } from 'react'
import { Outlet, useMatches } from 'react-router-dom'
import { SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/app-sidebar'
import { TopBar } from '@/components/top-bar'
import { VirtualPlaceChoiceHost } from '@/components/task-expense/VirtualPlaceChoiceDialog'
import { AssistantWindowProvider } from '@/components/ai-chat/AssistantWindowProvider'
import { EntityFlyoutProvider } from '@/components/entity-flyout/EntityFlyoutProvider'
import { AssistantDockInset } from '@/components/ai-chat/AssistantDockInset'
import { StackingLayerProvider } from '@/components/ui/stacking-layer'
import { ASSISTANT_TOPBAR_LAYER } from '@/features/ai-chat/pageModal'
import { NovuProviderWrapper } from '@/components/notifications'
import { isWebView } from '@/utils/webview'
import { Loader2 } from 'lucide-react'
import { SchemaPreloader } from '@/features/list-page/schema/SchemaPreloader'
import { ConfigVersionRevalidator } from '@/components/providers/config-version-revalidator'
import { RefreshHost } from '@/features/realtime/RefreshHost'
import { ChatProvider } from '@/features/chat/ChatProvider'
import { ChatPanelProvider } from '@/features/chat/panel/ChatPanelProvider'
import { CallProvider } from '@/contexts/CallContext'
import { IncomingCallPopup } from '@/components/calls/IncomingCallPopup'
import { ActiveCallBar } from '@/components/calls/ActiveCallBar'
import { CallStatusIcon } from '@/components/calls/CallStatusIcon'
import { RemindersHost } from '@/features/reminders/RemindersHost'
import { AnnouncementsHost } from '@/features/announcements/AnnouncementsHost'
import { AnnouncementNovuBridge } from '@/features/announcements/AnnouncementNovuBridge'

// 路由懒加载时的 fallback。整页居中转圈，不撑开布局。
const RouteFallback = () => (
  <div className="flex items-center justify-center w-full h-full min-h-[200px]">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
)

const Layout = () => {
  // 懒加载页面切换时必须立刻显示 fallback,而不是把旧页面挂着等新 chunk:
  // React Router v7 的导航默认包在 startTransition 里,而 transition 期间 React
  // 不会用 fallback 替换"已经显示出来"的 Suspense 内容 —— 于是 URL 已经变成新
  // 菜单、界面却还是旧页面,慢网/首访(dev 模式按需编译)下能卡好几秒且零反馈。
  // 给 Suspense 按「Layout 直接子路由」换 key:新页面 = 新边界,transition 下
  // 新边界允许立即显示 fallback。用直接子路由 id 而不是 pathname,保证同一路由
  // 内的参数变化(/tasks/1 → /tasks/2)和嵌套布局路由(catalogs/* 等)不被整体
  // 重挂。matches[0] 是本 Layout 所在的 "/" 路由,matches[1] 即直接子路由。
  const matches = useMatches()
  const pageKey = matches[1]?.id ?? 'root'

  // WebView 模式：只渲染页面内容，不要 sidebar/topbar/novu
  // App 端提供自己的导航栏
  if (isWebView()) {
    return (
      <>
        {/* Pre-warm entity schemas after auth so the first list page mounts with
            cached metadata. Side-effect only — renders nothing. */}
        <SchemaPreloader />
        {/* Route-change-driven config version revalidator. Must live inside
            the RouterProvider tree (it calls useLocation). See
            config-version-revalidator.tsx for rationale. */}
        <ConfigVersionRevalidator />
        <main className="bg-background h-screen w-full overflow-auto">
          <Suspense key={pageKey} fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </>
    )
  }

  return (
    // 🔧 NovuProviderWrapper 只包 TopBar（铃铛 + 收件箱是 Novu 上下文的唯一消费者）。
    // 它在 Redux userId 落地时会从 Fallback 树切换成 <NovuProvider key=...> 树 ——
    // 组件类型变化导致 children 整体 REMOUNT。之前它包住整个 Layout 内容，
    // 等价于"登录后 user 信息加载完成的瞬间，当前路由页被卸载重建"：
    // 用户已输入的一切（如 /workflows/new 编辑到一半的配置）全部清空，
    // dev API 慢时该窗口长达几十秒（2026-06-11 实锤，e2e 失败快照证实）。
    // 缩小包裹范围后，树切换的影响只剩 TopBar 自身重挂一次（纯展示，无输入态）。
    // 注意：业务页面里禁止直接使用 @novu/react 的 hooks（useNovu/useCounts 等），
    // 需要通知数据时走 TopBar 子树或新开稳定 context 桥接。
    // CallProvider 包裹整个 Layout（稳定 context，不做 Fallback→Provider 类型切换，
    // 故不会触发上面的 remount 问题），为来电/通话 overlays 提供上下文。
    <CallProvider>
      {/* Messages 状态（实时连接 / 未读数 / 会话列表）：包住顶栏入口与 /messages 页，稳定 context 不随 Novu 树重挂 */}
      <ChatProvider>
      {/* AI 助手浮窗：provider 要同时包住顶栏（入口按钮）与页面；浮窗本身 portal 到 body（F3） */}
      <EntityFlyoutProvider>
      <AssistantWindowProvider>
      {/* Messages 右侧侧边栏：与 AI 助手停靠面板同一套机制（同宽、页面让位），两者同一时间只开一个 */}
      <ChatPanelProvider>
        <SidebarProvider defaultOpen={true}>
          <RefreshHost />
          <SchemaPreloader />
          <ConfigVersionRevalidator />
          <div className="flex min-h-screen w-full">
            {/* Sidebar (left) — collapse-to-icon is now handled inside AppSidebar
                via SidebarProvider state (dev rewrite), so no `collapsible` prop. */}
            <AppSidebar />

            {/* Main content container (right) */}
            <div className="flex flex-col flex-1 max-w-full overflow-hidden">
              {/* TopBar - must be inside a non-scrollable parent */}
              <div className="flex-shrink-0 z-10 bg-background">
                {/* 顶栏弹出的菜单 / 面板盖在 AI 助手浮窗之上：浮窗默认贴在顶栏下方右侧，否则头像菜单等被它压住（F3 live 09-14） */}
                <StackingLayerProvider value={ASSISTANT_TOPBAR_LAYER}>
                  <NovuProviderWrapper>
                    <TopBar />
                    <AnnouncementNovuBridge />
                  </NovuProviderWrapper>
                </StackingLayerProvider>
              </div>

              {/* Main content area */}
              <AssistantDockInset>
                <main className="bg-background h-full">
                  <Suspense key={pageKey} fallback={<RouteFallback />}>
                    <Outlet />
                  </Suspense>
                </main>
              </AssistantDockInset>
            </div>
          </div>

          <VirtualPlaceChoiceHost />
          {/* Toaster 只在 app/providers.tsx 挂一次:这里再挂一个 sonner <Toaster>,每条 toast 会叠两层渲染 */}
          <RemindersHost />
          <AnnouncementsHost />
        </SidebarProvider>
      </ChatPanelProvider>
      </AssistantWindowProvider>
      </EntityFlyoutProvider>
      </ChatProvider>

      {/* Call overlays — rendered above all content, below toast */}
      <IncomingCallPopup />
      <ActiveCallBar />
      <CallStatusIcon />
    </CallProvider>
  )
}

export default Layout